import LoadingScreen from '@/components/LoadingScreen';
import ClassificationCombobox from '@/components/TaskHub/ClassificationCombobox.js';
import { Button, Input, Label, Tooltip } from '@/components/ui';
import {
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useKayAuth } from '@/context/AuthContext';
import isoglcodes from '@/data/isoglcodes';
import sicCodes from '@/data/sicCodes';
import backendApis from '@/services/backendApis';
import { WorkflowLogStatus, WorkflowRunListObject } from '@/types/workflowRuns';
import { useAuth } from '@clerk/clerk-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {Card, CardContent, CardHeader} from "@/components/ui/card.js";
import interactAgentTaskEvents from "@/constants/interactAgentTaskEvents.js";

type PreCheckListProps = {
  taskData: WorkflowRunListObject | null;
};

type FieldType = {
  confidence?: number;
  label: string;
  reasoning?: string;
  type?: string;
  value: string;
};

export enum SpecialFields {
  GL_ISO_CODE = 'gl_iso_code',
  SIC_CODE = 'sic_code',
}

export type SpecialField = SpecialFields.SIC_CODE | SpecialFields.GL_ISO_CODE;

const PreCheckList = (props: PreCheckListProps) => {
  const { user } = useKayAuth();
  const { getToken } = useAuth();
  const userId = user?.user_id;
  const [initiatedPrecheckDone, setInitiatedPrecheckDone] = useState(false);
  const { taskData } = props;

  const specialFields: SpecialField[] = [
    SpecialFields.SIC_CODE,
    SpecialFields.GL_ISO_CODE,
  ];

  const failedLog = taskData?.workflow_log?.workflow_log_records.find(
    (record) =>
      record.log_record_status === WorkflowLogStatus.PRECHECKLIST_FAILED,
  );

  const fields =
    failedLog?.log_record_payload?.job_staging?.job_result
      ?.update_requirements || [];

  const [sicCodeOptions, setSicCodeOptions] = useState<FieldType[]>([]);
  const [glIsoCodeOptions, setGlIsoCodeOptions] = useState<FieldType[]>([]);

  const [sicCodeSuggestions, setSicCodeSuggestions] = useState<FieldType[]>([]);
  const [glIsoCodeSuggestions, setGlIsoCodeSuggestions] = useState<FieldType[]>(
    [],
  );

  // Load and parse SIC code CSV file
  useEffect(() => {
    // Extract options for SIC and GL ISO codes from fields
    let sicCodeSuggestions = fields
      .filter((field: any) => field.type === SpecialFields.SIC_CODE)
      .map((field: FieldType) => ({
        label: field.label,
        reasoning: field.reasoning,
        value: field.value,
      }));
    let glIsoCodeSuggestions = fields
      .filter((field: any) => field.type === SpecialFields.GL_ISO_CODE)
      .map((field: FieldType) => ({
        label: field.label,
        reasoning: field.reasoning,
        value: field.value,
      }));

    const _sicCodesVar = sicCodes;
    const _IsoGlCodesVar = isoglcodes;

    const parsedSicCodes = _sicCodesVar.map((a: any) => ({
      label: a['Four Digit SIC Code Descriptions'],
      value: a['Four Digit SIC Codes'],
    }));
    const parsedGlCodes = _IsoGlCodesVar.map((a: any) => ({
      label: a['Description'],
      value: a['Code'],
    }));

    sicCodeSuggestions = sicCodeSuggestions.map((suggestion: any) => {
      const sicCode = parsedSicCodes.find(
        (code) => code.value == suggestion.value,
      );
      if (sicCode) {
        suggestion.label = sicCode.label;
      }
      return suggestion;
    });
    glIsoCodeSuggestions = glIsoCodeSuggestions.map((suggestion: any) => {
      const glIsoCode = parsedGlCodes.find(
        (code) => code.value == suggestion.value,
      );
      if (glIsoCode) {
        suggestion.label = glIsoCode.label;
      }
      return suggestion;
    });

    setSicCodeSuggestions(sicCodeSuggestions);
    setGlIsoCodeSuggestions(glIsoCodeSuggestions);
    setSicCodeOptions(parsedSicCodes);
    setGlIsoCodeOptions(parsedGlCodes);
  }, []);

  const isSicCodeField = sicCodeOptions.length > 0;

  const isGlIsoCodeField = glIsoCodeOptions.length > 0;
  // Prepare the fields for display, treating all non-special fields as text fields

  const [codeFields, setCodeFields] = useState<any[]>([]);
  const [editableFields, setEditableFields] = useState(
    fields
      .filter((item: any) => !specialFields.includes(item.type))
      .map((item: any) => {
        if (!specialFields.includes(item.type)) {
          return { ...item, type: 'text' };
        }
        return item;
      }),
  );

  // Handle field changes
  const handleChange = (index: number, value: string) => {
    const updatedFields = [...editableFields];
    updatedFields[index].value = value;
    setEditableFields(updatedFields);
  };

  // Send updated data to the backend
  const updateNewInfo = async (data: any) => {
    try {
      const token = await getToken();
      const params = {
        job_context_id:
          failedLog?.log_record_payload?.job_staging?.job_context_id,
        job_interaction: {
          action: interactAgentTaskEvents.UpdateDataFromResponse,
          payload: {
            updated_elements: data,
          },
        },
        user_id: userId,
        workflow_run_id: taskData?.workflow_run?.workflow_run_id,
      };
      const response = await backendApis.interact(params, token);
      if (response.err) {
        console.error('Error updating task data log:', response.err);
        toast.error('Error updating task data log');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error updating task data log:', error);
      toast.error('Error updating task data log');
      return false;
    }
  };

  const chooseClassificationCode = (
    type: SpecialField,
    label: string,
    value: string,
  ) => {
    // if a field is already present, update it else add a new field
    const index = codeFields.findIndex((field) => field.type === type);
    if (index !== -1) {
      const updatedFields = [...codeFields];
      updatedFields[index] = { label, type, value };
      setCodeFields(updatedFields);
    } else {
      setCodeFields([...codeFields, { label, type, value }]);
    }
  };

  // Handle form submission
  const onComplete = async () => {
    const finalFields = [...editableFields, ...codeFields];
    console.warn('Updating new info:', finalFields);
    if (
      isSicCodeField &&
      !finalFields.find(
        (field: FieldType) => field.type === SpecialFields.SIC_CODE,
      )
    ) {
      toast.error('Please fill the SIC Code field');
      return;
    }
    if (
      isGlIsoCodeField &&
      !finalFields.find(
        (field: FieldType) => field.type === SpecialFields.GL_ISO_CODE,
      )
    ) {
      toast.error('Please fill the GL ISO Code field');
      return;
    }
    const response = await updateNewInfo(finalFields);
    if (response) {
      setInitiatedPrecheckDone(true);
      setTimeout(() => setInitiatedPrecheckDone(false), 20_000);
    }
  };

  if (initiatedPrecheckDone) {
    return (
      <div className="flex flex-col max-w-2xl gap-4 p-6 mx-auto space-y-4 bg-white rounded-lg shadow-lg">
        <h1 className="text-2xl font-bold text-gray-800">Pre-Check List</h1>
        <LoadingScreen />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto shadow-lg">
      <Card className='bg-white'>
        <CardHeader>
          <h1 className="mb-1 text-2xl font-bold text-gray-800">Pre-Check List</h1>
        </CardHeader>

        <CardContent>
          <div className="space-y-6">
            <p className="mb-4 text-sm text-gray-600">
              Please verify the following information. Fields highlighted need your
              attention.
            </p>

            <div className="space-y-6">
              {/* SIC Code Combobox */}
              {sicCodeOptions.length > 0 && (
                <div className="space-y-2">
                  <Label className="block mb-2 text-sm font-medium text-gray-700">
                    SIC Code
                  </Label>
                  <ClassificationCombobox
                    chooseClassificationCode={chooseClassificationCode}
                    label="SIC Code"
                    name={SpecialFields.SIC_CODE}
                    options={sicCodeOptions}
                    suggestions={sicCodeSuggestions}
                  />
                </div>
              )}

              {/* GL ISO Code Combobox */}
              {glIsoCodeOptions.length > 0 && (
                <div className="space-y-2">
                  <Label className="block mb-2 text-sm font-medium text-gray-700">
                    GL ISO Code
                  </Label>
                  <ClassificationCombobox
                    chooseClassificationCode={chooseClassificationCode}
                    label="GL ISO Code"
                    name={SpecialFields.GL_ISO_CODE}
                    options={glIsoCodeOptions}
                    suggestions={glIsoCodeSuggestions}
                  />
                </div>
              )}

              {/* Display all other fields as text inputs */}
              {editableFields.map((field: FieldType, index: number) => {
                if (specialFields.includes(field.type as SpecialField)) {
                  return null;
                }

                return (
                  <div className="space-y-2" key={index}>
                    <div className="flex flex-row items-center gap-3">
                      <Label className="text-sm font-medium text-gray-700">
                        {field.label}
                      </Label>
                      {field.reasoning && (
                        <Tooltip>
                          <TooltipTrigger>
                          <span className="ml-1 text-xs text-blue-500 cursor-pointer hover:text-blue-700">
                            ℹ️
                          </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <span>{field.reasoning}</span>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    <Input
                      className={`w-full rounded-md border p-2 transition-all duration-200 ${
                        field.confidence < 0.5
                          ? 'border-red-500 bg-red-50 focus:ring-2 focus:ring-red-200'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                      }`}
                      onChange={(e) => handleChange(index, e.target.value)}
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      type="text"
                      value={field.value}
                    />
                  </div>
                );
              })}
            </div>

            <Button
              className="w-full py-3 mt-6 text-white transition-colors duration-300 rounded-md"
              onClick={onComplete}
            >
              Complete
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
};

export default PreCheckList;
